import React, { useState } from 'react';
import { AlertCircle, ClipboardList, History, Info, Loader2, Package, RefreshCw } from 'lucide-react';
import { useTranslation } from '../../../../contexts/I18nProvider';
import { BackButton } from '../../../common/BackButton';
import { useLocationOverview } from './useLocationOverview';
import { LocationHeader } from './sections/LocationHeader';
import { LocationItemsSection } from './sections/LocationItemsSection';
import { LocationOpenTasksSection } from './sections/LocationOpenTasksSection';
import { LocationTransactionsSection } from './sections/LocationTransactionsSection';
import { LocationDataSection } from './sections/LocationDataSection';

type TabId = 'items' | 'tasks' | 'history' | 'data';

interface LocationLookupViewProps {
  warehouseId: string;
  binCode: string;
  onBack: () => void;
  onOpenItem: (itemNo: string) => void;
}

/**
 * Lokasjonsoppslaget for én lokasjon: hode med nøkkeltall øverst, resten i faner som i varekortet —
 * Innhold (varene), Oppgaver (åpne til/fra lokasjonen), Historikk (siste bevegelser) og Lokasjonsdata.
 */
export const LocationLookupView: React.FC<LocationLookupViewProps> = ({ warehouseId, binCode, onBack, onOpenItem }) => {
  const { t } = useTranslation();
  const prefix = 'warehouse.modules.locationLookup';
  const { overview, loading, error, notFound, reload } = useLocationOverview(warehouseId, binCode);
  const [activeTab, setActiveTab] = useState<TabId>('items');

  const backLabel = t(`${prefix}.backToSearch`, {}, 'Ny lokasjon');

  if (loading && !overview) {
    return (
      <div className="space-y-4">
        <BackButton onClick={onBack} label={backLabel} />
        <div className="bg-white rounded-lg shadow-sm flex items-center justify-center py-16">
          <Loader2 className="h-6 w-6 animate-spin text-blue-500" />
        </div>
      </div>
    );
  }

  if (notFound || error || !overview || overview.isOperatorLocation) {
    return (
      <div className="space-y-4">
        <BackButton onClick={onBack} label={backLabel} />
        <div className="bg-white rounded-lg shadow-sm p-6 flex items-center gap-3 text-red-600">
          <AlertCircle className="h-5 w-5 flex-shrink-0" />
          <p className="text-sm flex-1">
            {overview?.isOperatorLocation
              ? t(`${prefix}.search.operatorLocation`, { '0': overview.bin.binCode }, `«${overview.bin.binCode}» er en operatørlokasjon og vises ikke i lokasjonsoppslaget`)
              : notFound
                ? t(`${prefix}.search.notFound`, { '0': binCode, '1': warehouseId }, `Fant ikke lokasjon «${binCode}» i lager ${warehouseId}`)
                : t(`${prefix}.loadError`, {}, 'Kunne ikke laste lokasjonen')}
          </p>
          {!notFound && !overview?.isOperatorLocation && (
            <button
              type="button"
              onClick={() => void reload()}
              className="inline-flex items-center gap-1.5 px-3 py-2 min-h-[44px] rounded-lg text-sm font-medium bg-gray-100 text-gray-700 hover:bg-gray-200"
            >
              <RefreshCw className="h-4 w-4" />
              {t(`${prefix}.retry`, {}, 'Prøv igjen')}
            </button>
          )}
        </div>
      </div>
    );
  }

  const { openTasks } = overview;
  const openTaskCount = openTasks.picking.length + openTasks.putAwayReplenishment.length
    + openTasks.awaitingPutAway.length + openTasks.pendingTransactions.length;

  const tabs: { id: TabId; label: string; icon: React.ReactNode; badge?: number }[] = [
    { id: 'items', label: t(`${prefix}.tabs.items`, {}, 'Innhold'), icon: <Package className="h-4 w-4" />, badge: overview.totals.itemCount },
    { id: 'tasks', label: t(`${prefix}.tabs.tasks`, {}, 'Oppgaver'), icon: <ClipboardList className="h-4 w-4" />, badge: openTaskCount },
    { id: 'history', label: t(`${prefix}.tabs.history`, {}, 'Historikk'), icon: <History className="h-4 w-4" />, badge: overview.recentTransactions.length },
    { id: 'data', label: t(`${prefix}.tabs.data`, {}, 'Lokasjonsdata'), icon: <Info className="h-4 w-4" /> },
  ];

  return (
    <div className="space-y-3 sm:space-y-4">
      <div className="flex items-center justify-between gap-3">
        <BackButton onClick={onBack} label={backLabel} />
        <button
          type="button"
          onClick={() => void reload()}
          disabled={loading}
          className="inline-flex items-center gap-1.5 px-3 py-2 min-h-[44px] rounded-lg text-sm font-medium text-gray-600 hover:bg-gray-100 disabled:opacity-50"
          title={t(`${prefix}.refresh`, {}, 'Oppdater')}
        >
          <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
          <span className="hidden sm:inline">{t(`${prefix}.refresh`, {}, 'Oppdater')}</span>
        </button>
      </div>

      <LocationHeader bin={overview.bin} totals={overview.totals} />

      {/* Fanelinje — samme uttrykk som varekortet */}
      <div className="bg-white rounded-lg shadow-sm overflow-hidden">
        <nav className="flex w-full border-b border-gray-200" role="tablist">
          {tabs.map(tab => {
            const isActive = tab.id === activeTab;
            return (
              <button
                key={tab.id}
                type="button"
                role="tab"
                aria-selected={isActive}
                onClick={() => setActiveTab(tab.id)}
                className={`flex-1 sm:flex-none flex items-center justify-center gap-1.5 px-2 sm:px-4 py-3 min-h-[48px] text-xs sm:text-sm font-medium text-center leading-tight border-b-2 -mb-px sm:whitespace-nowrap transition-colors ${
                  isActive
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                <span className="hidden sm:inline-flex">{tab.icon}</span>
                <span>{tab.label}</span>
                {tab.badge !== undefined && tab.badge > 0 && (
                  <span className={`text-xs font-medium px-1.5 py-0.5 rounded-full ${isActive ? 'bg-blue-100 text-blue-700' : 'bg-gray-100 text-gray-600'}`}>
                    {tab.badge}
                  </span>
                )}
              </button>
            );
          })}
        </nav>
      </div>

      {activeTab === 'items' && <LocationItemsSection items={overview.items} onOpenItem={onOpenItem} />}
      {activeTab === 'tasks' && <LocationOpenTasksSection tasks={overview.openTasks} binCode={overview.bin.binCode} />}
      {activeTab === 'history' && <LocationTransactionsSection transactions={overview.recentTransactions} />}
      {activeTab === 'data' && <LocationDataSection bin={overview.bin} />}
    </div>
  );
};
